import { MoneyMapCategoryToAccountColorMap } from '@fruitful/money-map';
import {
    HolisticCategory,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountUpsertDto,
    type MoneyMapCashAccountCreateParameters,
    MoneyMapCategory,
    MoneyMapRuleType,
    type MoneyMapRuleUpsertDto,
    MoneyMapStatus,
    type MoneyMapUpsertDto,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { FRUITFUL_ICON_KEY } from 'smoothie';

import type { MembershipDetails } from '../../entities/entityTypes';
import type { ResolvedMoneyMapperDefaults } from './moneyMapperDefaultValues';
import {
    getAccountOwnershipAndPrimaryUser,
    getMoneyMapDefaultName,
} from './moneyMapUtils';

const buildCoreAccountCreateParameters = (
    membership: MembershipDetails,
): MoneyMapCashAccountCreateParameters => {
    const { ownership, primaryUserId } =
        getAccountOwnershipAndPrimaryUser(membership);
    return {
        name: 'Core',
        ownership,
        primaryUserId,
        category: HolisticCategory.SPEND,
        virtualCard: true,
        physicalCard: false,
        icon: FRUITFUL_ICON_KEY,
        color: MoneyMapCategoryToAccountColorMap[
            MoneyMapCategory.BILLS
        ] as string,
    };
};

const buildSpendAccountCreateParameters = (
    membership: MembershipDetails,
): MoneyMapCashAccountCreateParameters => {
    const { ownership, primaryUserId } =
        getAccountOwnershipAndPrimaryUser(membership);
    return {
        name: 'Spend',
        ownership,
        primaryUserId,
        category: HolisticCategory.SPEND,
        virtualCard: true,
        physicalCard: true,
        icon: FRUITFUL_ICON_KEY,
        color: MoneyMapCategoryToAccountColorMap[
            MoneyMapCategory.SPEND
        ] as string,
    };
};

const buildNewCashAccountUpsertDto = (
    membership: MembershipDetails,
    createParameters: MoneyMapCashAccountCreateParameters,
): MoneyMapAccountUpsertDto => {
    return {
        membershipId: membership.id,
        type: MoneyMapAccountType.INTERNAL_BANKING,
        status: MoneyMapAccountStatus.PROPOSED,
        createParameters,
    };
};

/**
 * Builds a MoneyMapUpsertDto for the Money Mapper draft: income account (Core),
 * bills rule (Core), and spend rule (Spend). No goal rules.
 */
export const buildMoneyMapperDraftDto = (
    membership: MembershipDetails,
    defaults: ResolvedMoneyMapperDefaults,
): MoneyMapUpsertDto => {
    const coreCreateParams = buildCoreAccountCreateParameters(membership);
    const spendCreateParams = buildSpendAccountCreateParameters(membership);

    const coreAccount = buildNewCashAccountUpsertDto(
        membership,
        coreCreateParams,
    );

    const spendAccount = buildNewCashAccountUpsertDto(
        membership,
        spendCreateParams,
    );

    // BILLS monthly amount is stored as MINIMUM_BALANCE/targetBalanceCents;
    // SPEND as PERCENTAGE/monthlyContributionMaxCents. See PaycheckRoutingRuleType.
    const billsRule: MoneyMapRuleUpsertDto = {
        type: MoneyMapRuleType.BILLS,
        description: 'Bills',
        priority: 1,
        routingRuleType: PaycheckRoutingRuleType.MINIMUM_BALANCE,
        targetBalanceCents: defaults.monthlyBillsCents,
        moneyMapAccount: coreAccount,
    };

    const spendRule: MoneyMapRuleUpsertDto = {
        type: MoneyMapRuleType.SPEND,
        description: 'Spend',
        priority: 2,
        routingRuleType: PaycheckRoutingRuleType.PERCENTAGE,
        percentage: 1,
        monthlyContributionMaxCents: defaults.monthlySpendCents,
        moneyMapAccount: spendAccount,
    };

    return {
        membershipId: membership.id,
        title: getMoneyMapDefaultName(membership),
        status: MoneyMapStatus.DRAFT,
        monthlyIncomeCents: defaults.monthlyIncomeCents,
        incomeMoneyMapAccount: coreAccount,
        moneyMapRules: [billsRule, spendRule],
    };
};
